import React, { useEffect, useState } from 'react';
import { getPendingLeaves, approveLeave, rejectLeave, getLeaves, deleteLeave, getAllStudents, deleteStudent } from '../services/api';

const AdminDashboard = ({ token, role }) => {
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [historicalLeaves, setHistoricalLeaves] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [comments, setComments] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [studentFilter, setStudentFilter] = useState('');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [pendingResponse, historyResponse, studentResponse] = await Promise.all([
          getPendingLeaves(token),
          getLeaves(token),
          getAllStudents(token),
        ]);

        console.log('Pending Leaves Response:', pendingResponse.leaves);
        console.log('History Leaves Response:', historyResponse.leaves);

        // Ensure that pendingLeaves only contains leaves with 'pending' status
        const pendingLeavesData = pendingResponse.leaves.filter(leave => leave.approval_status === 'pending');
        setPendingLeaves(pendingLeavesData);
        setHistoricalLeaves(historyResponse.leaves);
        setStudents(studentResponse.students);

        calculateStats(historyResponse.leaves);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const calculateStats = (leaves) => {
    const studentStats = {};
    let totalApproved = 0;
    let totalRejected = 0;
    let totalPending = 0;

    leaves.forEach(leave => {
      if (!studentStats[leave.student_id]) {
        studentStats[leave.student_id] = {
          name: leave.student_name,
          approved: 0,
          rejected: 0,
          pending: 0,
          total: 0
        };
      }

      studentStats[leave.student_id].total++;
      if (leave.status === 'approved') {
        studentStats[leave.student_id].approved++;
        totalApproved++;
      } else if (leave.status === 'rejected') {
        studentStats[leave.student_id].rejected++;
        totalRejected++;
      } else {
        studentStats[leave.student_id].pending++;
        totalPending++;
      }
    });

    setStats({
      students: Object.values(studentStats),
      totalApproved,
      totalRejected,
      totalPending,
      totalLeaves: leaves.length
    });
  };

  const handleApprove = async (leaveId, action) => {
    try {
      if (action === 'approve') {
        await approveLeave(token, leaveId, comments);
      } else {
        await rejectLeave(token, leaveId, comments);
      }

      const pendingResponse = await getPendingLeaves(token);
      const historyResponse = await getLeaves(token);

      console.log('Pending Leaves Response:', pendingResponse.leaves);
      console.log('History Leaves Response:', historyResponse.leaves);

      // Ensure that pendingLeaves only contains leaves with 'pending' status
      const pendingLeavesData = pendingResponse.leaves.filter(leave => leave.approval_status === 'pending');
      setPendingLeaves(pendingLeavesData);
      setHistoricalLeaves(historyResponse.leaves);
      calculateStats(historyResponse.leaves);

      setSelectedLeave(null);
      setComments('');
      alert(`Leave ${action}d successfully`);
    } catch (error) {
      console.error(`Error ${action}ing leave:`, error);
      alert('Failed to process leave request');
    }
  };

  const handleDeleteLeave = async (leaveId) => {
    try {
      await deleteLeave(token, leaveId);

      const pendingResponse = await getPendingLeaves(token);
      const historyResponse = await getLeaves(token);

      console.log('Pending Leaves Response:', pendingResponse.leaves);
      console.log('History Leaves Response:', historyResponse.leaves);

      // Ensure that pendingLeaves only contains leaves with 'pending' status
      const pendingLeavesData = pendingResponse.leaves.filter(leave => leave.approval_status === 'pending');
      setPendingLeaves(pendingLeavesData);
      setHistoricalLeaves(historyResponse.leaves);
      calculateStats(historyResponse.leaves);

      alert('Leave deleted successfully');
    } catch (error) {
      console.error('Error deleting leave:', error);
      alert('Failed to delete leave');
    }
  };

  const handleDeleteStudent = async (studentId) => {
    try {
      await deleteStudent(token, studentId);

      const studentResponse = await getAllStudents(token);
      setStudents(studentResponse.students);

      alert('Student deleted successfully');
    } catch (error) {
      console.error('Error deleting student:', error);
      alert('Failed to delete student');
    }
  };

  const filteredLeaves = activeTab === 'pending'
    ? pendingLeaves.filter(leave =>
        studentFilter ? leave.student_name.toLowerCase().includes(studentFilter.toLowerCase()) : true
      )
    : historicalLeaves.filter(leave =>
        studentFilter ? leave.student_name.toLowerCase().includes(studentFilter.toLowerCase()) : true
      );

  if (loading) {
    return <div style={styles.loading}>Loading leave data...</div>;
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Leave Management Dashboard</h2>

      <div style={styles.tabContainer}>
        <button
          style={activeTab === 'pending' ? styles.activeTab : styles.tab}
          onClick={() => setActiveTab('pending')}
        >
          {role === 'admin' ? 'Admin Approvals' : 'Pending Approvals'}
          {pendingLeaves.length > 0 && (
            <span style={styles.badge}>{pendingLeaves.length}</span>
          )}
        </button>
        <button
          style={activeTab === 'history' ? styles.activeTab : styles.tab}
          onClick={() => setActiveTab('history')}
        >
          Leave History
        </button>
        <button
          style={activeTab === 'students' ? styles.activeTab : styles.tab}
          onClick={() => setActiveTab('students')}
        >
          Students
        </button>
      </div>

      <div style={styles.filterContainer}>
        <input
          type="text"
          placeholder="Filter by student name..."
          style={styles.filterInput}
          value={studentFilter}
          onChange={(e) => setStudentFilter(e.target.value)}
        />
      </div>

      {activeTab === 'history' && stats && (
        <div style={styles.statsContainer}>
          <div style={styles.statCard}>
            <h4>Total Leaves</h4>
            <p style={styles.statNumber}>{stats.totalLeaves}</p>
          </div>
          <div style={{...styles.statCard, backgroundColor: '#e8f5e9'}}>
            <h4>Approved</h4>
            <p style={styles.statNumber}>{stats.totalApproved} ({Math.round((stats.totalApproved/stats.totalLeaves)*100)}%)</p>
          </div>
          <div style={{...styles.statCard, backgroundColor: '#ffebee'}}>
            <h4>Rejected</h4>
            <p style={styles.statNumber}>{stats.totalRejected} ({Math.round((stats.totalRejected/stats.totalLeaves)*100)}%)</p>
          </div>
          <div style={{...styles.statCard, backgroundColor: '#fff8e1'}}>
            <h4>Pending</h4>
            <p style={styles.statNumber}>{stats.totalPending} ({Math.round((stats.totalPending/stats.totalLeaves)*100)}%)</p>
          </div>
        </div>
      )}

      {activeTab === 'history' && stats && (
        <div style={styles.studentStats}>
          <h3 style={styles.subtitle}>Student Statistics</h3>
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Student</th>
                  <th style={styles.th}>Total</th>
                  <th style={styles.th}>Approved</th>
                  <th style={styles.th}>Rejected</th>
                  <th style={styles.th}>Pending</th>
                  <th style={styles.th}>Approval Rate</th>
                </tr>
              </thead>
              <tbody>
                {stats.students.map(student => (
                  <tr key={student.name} style={styles.tr}>
                    <td style={styles.td}>{student.name}</td>
                    <td style={styles.td}>{student.total}</td>
                    <td style={styles.td}>{student.approved}</td>
                    <td style={styles.td}>{student.rejected}</td>
                    <td style={styles.td}>{student.pending}</td>
                    <td style={styles.td}>
                      {student.total > 0 ? Math.round((student.approved/student.total)*100) : 0}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'students' && students && (
        <div>
          <h3>All Students</h3>
          <ul>
            {students.map((s) => (
              <li key={s.id} style={styles.studentItem}>
                {s.first_name} {s.last_name} {s.username}
                <button
                  style={{...styles.actionButton, backgroundColor: 'red'}}
                  onClick={() => handleDeleteStudent(s.id)}
                >
                  Delete Student
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div style={styles.leaveListContainer}>
        {filteredLeaves.length === 0 ? (
          <p style={styles.noLeaves}>No leaves found</p>
        ) : (
          filteredLeaves.map((leave) => (
            <div key={leave.id} style={styles.leaveCard}>
              <div style={styles.leaveHeader}>
                <div>
                  <h3 style={styles.studentName}>{leave.student_name}</h3>
                  <p style={styles.leaveMeta}>
                    <span style={styles.leaveType}>{leave.leave_type}</span>
                    <span style={styles.leaveDate}>{leave.created_at}</span>
                  </p>
                </div>
                <span style={{
                  ...styles.statusBadge,
                  backgroundColor: leave.status === 'approved' ? '#4CAF50' :
                                    leave.status === 'rejected' ? '#f44336' : '#FFC107',
                  color: leave.status === 'pending' ? '#000' : '#fff'
                }}>
                  {leave.status}
                </span>
              </div>

              <div style={styles.leaveDetails}>
                <p><strong>Dates:</strong> {leave.start_date} to {leave.end_date}</p>
                <p><strong>Reason:</strong> {leave.reason}</p>
                <p><strong>Student Email:</strong> {leave.student_email}</p>
                <p><strong>Admission Stream:</strong> {leave.admission_stream}</p>
                <p><strong>Class:</strong> {leave.class}</p>
                <p><strong>Next of Kin Phone:</strong> {leave.next_of_kin_phone_number}</p>
                <p><strong>Relationship:</strong> {leave.relationship}</p>

                {leave.professor_approval_status && (
                  <p><strong>Your Action:</strong> {leave.professor_approval_status}</p>
                )}

                {leave.reviewed_by && (
                  <p><strong>Reviewed By:</strong> {leave.reviewed_by}</p>
                )}
              </div>

              {activeTab === 'pending' && selectedLeave === leave.id ? (
                <div style={styles.approvalForm}>
                  <div style={styles.buttonGroup}>
                    <button
                      style={{...styles.button, ...styles.approveButton}}
                      onClick={() => handleApprove(leave.id, 'approve')}
                    >
                      Approve
                    </button>
                    <button
                      style={{...styles.button, ...styles.rejectButton}}
                      onClick={() => handleApprove(leave.id, 'reject')}
                    >
                      Reject
                    </button>
                    <button
                      style={{...styles.button, ...styles.cancelButton}}
                      onClick={() => setSelectedLeave(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {activeTab === 'pending' && (
                    <button
                      style={styles.actionButton}
                      onClick={() => setSelectedLeave(leave.id)}
                    >
                      Review Leave
                    </button>
                  )}
                  <button
                    style={{...styles.actionButton, backgroundColor: 'green'}}
                    onClick={() => handleDeleteLeave(leave.id)}
                  >
                    Delete Leave
                  </button>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '2rem',
    fontFamily: "'Georgia', serif",
    maxWidth: '1200px',
    margin: '0 auto',
    backgroundColor: '#f9f9f9',
    minHeight: '100vh',
  },
  title: {
    color: '#A51C30',
    marginBottom: '1.5rem',
    textAlign: 'center',
    fontSize: '2rem',
    fontWeight: '600',
  },
  subtitle: {
    color: '#333',
    margin: '1.5rem 0',
    fontSize: '1.3rem',
    borderBottom: '2px solid #A51C30',
    paddingBottom: '0.5rem',
  },
  loading: {
    textAlign: 'center',
    fontSize: '1.2rem',
    margin: '2rem',
    color: '#666',
  },
  tabContainer: {
    display: 'flex',
    marginBottom: '1.5rem',
    borderBottom: '1px solid #ddd',
  },
  tab: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#f0f0f0',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1rem',
    marginRight: '0.5rem',
    borderRadius: '4px 4px 0 0',
    transition: 'all 0.3s ease',
  },
  activeTab: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#A51C30',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1rem',
    marginRight: '0.5rem',
    borderRadius: '4px 4px 0 0',
    fontWeight: '500',
  },
  badge: {
    backgroundColor: '#fff',
    color: '#A51C30',
    borderRadius: '50%',
    padding: '0.2rem 0.5rem',
    fontSize: '0.8rem',
    marginLeft: '0.5rem',
    fontWeight: 'bold',
  },
  filterContainer: {
    marginBottom: '1.5rem',
  },
  filterInput: {
    padding: '0.75rem',
    width: '100%',
    maxWidth: '400px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '1rem',
  },
  statsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '1rem',
    marginBottom: '2rem',
  },
  statCard: {
    backgroundColor: '#fff',
    padding: '1rem',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    textAlign: 'center',
  },
  statNumber: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    margin: '0.5rem 0 0',
    color: '#333',
  },
  studentStats: {
    marginBottom: '2rem',
  },
  tableContainer: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: '#fff',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  th: {
    backgroundColor: '#A51C30',
    color: 'white',
    padding: '1rem',
    textAlign: 'left',
  },
  tr: {
    borderBottom: '1px solid #eee',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  td: {
    padding: '1rem',
    color: '#555',
  },
  leaveListContainer: {
    display: 'grid',
    gap: '1.5rem',
  },
  noLeaves: {
    textAlign: 'center',
    color: '#666',
    fontSize: '1.1rem',
    padding: '2rem',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  leaveCard: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    padding: '1.5rem',
    borderLeft: '4px solid #A51C30',
  },
  leaveHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1rem',
    borderBottom: '1px solid #eee',
    paddingBottom: '0.5rem',
  },
  studentName: {
    color: '#333',
    margin: '0 0 0.5rem 0',
    fontSize: '1.2rem',
  },
  leaveMeta: {
    display: 'flex',
    gap: '1rem',
    margin: 0,
    color: '#666',
    fontSize: '0.9rem',
  },
  leaveType: {
    backgroundColor: '#f0f0f0',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
  },
  leaveDate: {
    color: '#666',
  },
  statusBadge: {
    padding: '0.25rem 0.75rem',
    borderRadius: '12px',
    fontSize: '0.8rem',
    fontWeight: '500',
  },
  leaveDetails: {
    marginBottom: '1.5rem',
    color: '#555',
    '& p': {
      margin: '0.5rem 0',
    },
  },
  actionButton: {
    backgroundColor: '#A51C30',
    color: '#fff',
    border: 'none',
    padding: '0.75rem 1rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    width: '20%',
    transition: 'background-color 0.3s ease',
    '&:hover': {
      backgroundColor: '#8c1626',
    },
  },
  approvalForm: {
    marginTop: '1rem',
  },
  commentsInput: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    marginBottom: '1rem',
    minHeight: '80px',
    fontFamily: "'Georgia', serif",
    fontSize: '1rem',
  },
  buttonGroup: {
    display: 'flex',
    gap: '1rem',
  },
  button: {
    flex: 1,
    padding: '0.75rem',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '500',
    transition: 'all 0.3s ease',
  },
  approveButton: {
    backgroundColor: '#4CAF50',
    color: 'white',
    '&:hover': {
      backgroundColor: '#3e8e41',
    },
  },
  rejectButton: {
    backgroundColor: '#f44336',
    color: 'white',
    '&:hover': {
      backgroundColor: '#d32f2f',
    },
  },
  cancelButton: {
    backgroundColor: '#e0e0e0',
    color: '#333',
    '&:hover': {
      backgroundColor: '#bdbdbd',
    },
  },
  studentItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem',
    borderBottom: '1px solid #eee',
  },
};

export default AdminDashboard;
